import React, { useEffect, useState } from "react"
import { useRouter } from "next/router"
import Image from "next/image"
import clsx from "clsx"

import { Risebinar } from "@interfaces/risebinar"
import { NO_DATA } from "@const/pictures"
import RisebinarServices, { RisebinarQuery } from "@services/risebinar.services"
import { RegularButton } from "@components/button"
import { RisebinarCard } from "@components/card"
import { Container, FlexBox, LoadingBox } from "@components/wrapper"
import { PaginationData } from "@interfaces/misc"
import { Heading } from "@components/typography"

interface RisebinarColumnProps {
  initialData: Risebinar[]
}

export function RisebinarColumn({ initialData }: RisebinarColumnProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadMore, setIsLoadMore] = useState(false)
  const [risebinars, setRisebinars] = useState<Risebinar[]>(initialData)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalData, setTotalData] = useState(0)

  const router = useRouter()
  const risebinar = new RisebinarServices()
  const query = router.query as unknown as RisebinarQuery
  const isReady = router.isReady

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true)
        const res = await risebinar.getRisebinarList(query)

        if (res?.isSuccess) {
          const data: PaginationData<Risebinar> = res.getValue()

          if (data.currentPage === 1) {
            setRisebinars(data.data)
          } else {
            const arr = risebinars.concat(data.data)
            setRisebinars(arr)
          }

          setCurrentPage(data.currentPage)
          setTotalData(data.totalData)
        } else {
          setRisebinars([])
          setCurrentPage(1)
        }

        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        setRisebinars([])
        setCurrentPage(1)
      }
    }

    if (isReady) {
      getData()
    }
  }, [query])

  const handleNext = async () => {
    setIsLoadMore(true)
    const res = await risebinar.getRisebinarList({ ...query, page: currentPage + 1 })

    if (res.isSuccess) {
      const result = res.getValue()
      const data = result.data
      console.log({ data })
      setRisebinars([...risebinars, ...result.data])
      setCurrentPage(currentPage + 1)
    }
    setIsLoadMore(false)
  }

  return (
    <Container className="pb-20">
      <Heading headingLevel="subtitle" className={clsx("sm:mb-5")}>
        Event & Workshop
      </Heading>
      {isLoading ? (
        <LoadingBox height="h-[50vh]" />
      ) : risebinars?.length ? (
        <div
          className={clsx(
            "grid md:grid-cols-2 lg:grid-cols-3 gap-7 mt-6 sm:mb-12",
            "grid-cols-1 mb-6"
          )}
        >
          {risebinars.map((value, index) => (
            <RisebinarCard data={value} key={index} index={index} />
          ))}
        </div>
      ) : (
        <FlexBox direction="col" className={clsx("items-center sm:pt-28", "pt-12 px-2")}>
          <Image src={NO_DATA} width="450" height="350" alt="Empty event or workshop" />
          <h3 className="mt-6 font-semibold sm:text-2xl text-md">
            Tidak ada event/workshop saat ini
          </h3>
        </FlexBox>
      )}

      {totalData > risebinars?.length && (
        <FlexBox className="flex justify-center">
          <RegularButton
            color="green"
            variant="solid"
            onClick={handleNext}
            isSubmitting={isLoadMore}
          >
            Tampilkan lebih banyak
          </RegularButton>
        </FlexBox>
      )}
    </Container>
  )
}
